import { useGetIdentity, useLogout } from '@refinedev/core'
import { useBreadcrumb } from '@refinedev/core'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Moon, Sun, Monitor, LogOut, ChevronRight, Menu } from 'lucide-react'
import { useTheme, type ThemeMode } from '@/providers/themeProvider'
import { cn } from '@/lib/utils'

const THEME_CYCLE: ThemeMode[] = ['light', 'dark', 'system']

const THEME_META: Record<ThemeMode, { icon: typeof Sun; label: string }> = {
  light:  { icon: Sun,     label: 'Light'  },
  dark:   { icon: Moon,    label: 'Dark'   },
  system: { icon: Monitor, label: 'System' },
}

interface HeaderProps {
  onMenuClick?: () => void
}

export function Header({ onMenuClick }: HeaderProps) {
  const { data: identity } = useGetIdentity<{ username: string; email: string }>()
  const { mutate: logout } = useLogout()
  const { breadcrumbs } = useBreadcrumb()
  const { mode, setMode } = useTheme()

  const { icon: ThemeIcon, label: themeLabel } = THEME_META[mode]

  const cycleTheme = () => {
    const idx = THEME_CYCLE.indexOf(mode)
    setMode(THEME_CYCLE[(idx + 1) % THEME_CYCLE.length])
  }

  return (
    <header className="flex items-center gap-3 h-16 px-6 shrink-0 border-b border-slate-100/60 dark:border-white/5">
      {onMenuClick && (
        <button
          type="button"
          className="md:hidden shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          onClick={onMenuClick}
          aria-label="Open navigation"
        >
          <Menu size={16} />
        </button>
      )}

      <nav className="flex-1 flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 min-w-0">
        {breadcrumbs.map((crumb, i) => (
          <span key={i} className="flex items-center gap-1 min-w-0">
            {i > 0 && <ChevronRight size={12} className="shrink-0 text-slate-300 dark:text-slate-600" />}
            <span className={
              i === breadcrumbs.length - 1
                ? 'text-slate-800 dark:text-slate-100 font-semibold truncate'
                : 'truncate'
            }>
              {crumb.label}
            </span>
          </span>
        ))}
      </nav>

      <div className="flex items-center gap-2">
        {/* 3-mode theme toggle button — click cycles light → dark → system → … */}
        <button
          type="button"
          onClick={cycleTheme}
          title={`Theme: ${themeLabel} — click to cycle`}
          aria-label={`Current theme: ${themeLabel}. Click to cycle.`}
          className={cn(
            'relative inline-flex h-8 items-center gap-1.5 rounded-xl px-2.5',
            'text-xs font-semibold text-slate-500 dark:text-slate-400',
            'bg-slate-100/70 dark:bg-white/6',
            'border border-slate-200/60 dark:border-white/8',
            'hover:bg-slate-200/70 dark:hover:bg-white/10',
            'transition-all duration-150 select-none',
          )}
        >
          <ThemeIcon size={13} className="shrink-0" />
          <span className="hidden sm:inline">{themeLabel}</span>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger className="cursor-pointer">
            <Avatar className="h-8 w-8 border border-slate-200 dark:border-slate-700">
              <AvatarFallback className="bg-[#076698] text-white text-xs font-bold">
                {identity?.username?.slice(0, 2).toUpperCase() ?? 'AD'}
              </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem className="text-xs text-muted-foreground" disabled>
              {identity?.email}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => logout()}>
              <LogOut size={14} className="mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
